import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * Full-width "Client" card at the top of the "Sync EHR data" modal (Figma
 * 16770:502448): initials avatar, a "Client" caption and "Name · email".
 *
 * Not the shared `ClientBadge`: that is the compact white chip of the detail
 * pages (24px avatar, no email); this one is a tinted, bordered, full-width
 * row. Renders nothing without a name.
 */
type Props = {
  name?: string | null;
  email?: string | null;
};

const KeragonClientCard = ({ name, email }: Props) => {
  const theme: Theme = useTheme();
  const fullName = (name ?? "").trim();
  if (!fullName) return null;

  const initials = fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  const line = [fullName, (email ?? "").trim()].filter(Boolean).join(" · ");

  return (
    <Box sx={styles.container(theme)}>
      <Box sx={styles.avatar(theme)}>{initials}</Box>
      <Box sx={styles.textBlock}>
        <Box sx={styles.caption(theme)}>Client</Box>
        <Box sx={styles.line(theme)}>{line}</Box>
      </Box>
    </Box>
  );
};

export default KeragonClientCard;

const styles = {
  // Figma #F7F9FA has no token; N30 is the nearest neutral tint.
  container: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colorTokens.N[50]}`,
    backgroundColor: theme.colorTokens.N[30],
    width: "100%",
    boxSizing: "border-box" as "border-box",
  }),
  avatar: (theme: Theme) => ({
    width: "32px",
    height: "32px",
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: theme.colorTokens.N[50],
    color: theme.colorTokens.N[80],
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  textBlock: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
    minWidth: 0,
  },
  caption: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "14px",
    color: theme.colorTokens.N[80],
  }),
  line: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[90],
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};
